// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type {
  SchedulingClient,
  SchedulingScope,
  SchedulingTransport,
  SchedulingTransportOptions,
} from '@polarissleep/scheduling-next';
import { createSchedulingClient } from '@polarissleep/scheduling-next';
import { getProviderAppEnvConfig } from '../config/env';

export function getProviderSchedulingScope(): SchedulingScope {
  const config = getProviderAppEnvConfig();
  if (!config.medplumOrganizationId || !config.polarisEnv) {
    throw new Error('Provider v2 scheduling requires a tenant organization and Polaris environment.');
  }
  return { organizationId: config.medplumOrganizationId, environment: config.polarisEnv };
}

export function createProviderSchedulingClient(medplum: MedplumClient): SchedulingClient {
  const transport: SchedulingTransport = {
    get: (path, options) => medplum.get(toMedplumUrl(medplum, path), toRequestOptions(options)),
    post: (path, body, options) =>
      medplum.post(toMedplumUrl(medplum, path), body, 'application/fhir+json', toRequestOptions(options)),
  };
  return createSchedulingClient(transport);
}

function toMedplumUrl(medplum: MedplumClient, path: string): URL {
  const parsed = new URL(path, 'https://provider-scheduling.invalid');
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments[0] !== 'R4' || segments.length < 2) {
    throw new Error(`Unexpected scheduling operation path: ${path}`);
  }
  const url = medplum.fhirUrl(...segments.slice(1));
  url.search = parsed.search;
  return url;
}

function toRequestOptions(options: SchedulingTransportOptions | undefined): RequestInit | undefined {
  return options?.signal ? { signal: options.signal } : undefined;
}
